import { useState, useEffect, useCallback, Fragment } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import Nodata from '../../../components/common/NoData';
import { usePermissions } from '../../../context/PermissionsContext';
import {
  getClassroomAssignmentSubmissions,
  gradeClassroomAssignmentSubmission,
  getClassroomAssignmentDetails,
  userAssignmentStatus
} from '../../../services/adminService';

function ClassroomAssignmentResponse({decodedClassroomId, decodedAssignmentId}) {
  const { permissions } = usePermissions();
  // console.log('decodedClassroomId',decodedClassroomId);
  // console.log('decodedAssignmentId',decodedAssignmentId);

  // State management
  const [submissions, setSubmissions] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [assignmentDetails, setAssignmentDetails] = useState(null);

  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  // Fetch assignment details
  const fetchAssignmentDetails = useCallback(async () => {
    if (!decodedClassroomId || !decodedAssignmentId) return;

    try {
      const response = await getClassroomAssignmentDetails({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId
      });

      if (response && response.success) {
        setAssignmentDetails(response.data.assignment || {});
      } else {
        toast.error(response?.message || 'Failed to fetch assignment details');
      }
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      toast.error('Error fetching assignment details');
    }
  }, [decodedClassroomId, decodedAssignmentId]);

  // Fetch submissions
  const fetchSubmissions = useCallback(async () => {
    if (!decodedClassroomId || !decodedAssignmentId) return;

    setLoading(true);
    try {
      const response = await getClassroomAssignmentSubmissions({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        page: page,
        limit: limit,
        search: searchTerm
      });

      // console.log("Get submissions response:", response);

      if (response && response.data) {
        const submissionsWithAnswers = response.data.map(submission => ({
          ...submission,
          questions: submission.answers || []
        }));

        setSubmissions(submissionsWithAnswers);

        if (response.pagination) {
          setTotalPages(response.pagination.totalPages || 1);
          setTotalSubmissions(response.pagination.total || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Error fetching submissions');
    } finally {
      setLoading(false);
    }
  }, [decodedClassroomId, decodedAssignmentId, page, limit, searchTerm]);

  // Initialize data
  useEffect(() => {
    if (decodedClassroomId && decodedAssignmentId) {
      fetchAssignmentDetails();
      fetchSubmissions();
    }
  }, [decodedClassroomId, decodedAssignmentId, fetchAssignmentDetails, fetchSubmissions]);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  // Toggle row expansion
  const toggleRowExpand = (userId) => {
    if (!permissions.classroom_assignment_response_view_analytics) {
      toast.warning("You don't have permission to view response details");
      return;
    }
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Handle marking answer as wrong
  const handleMarkWrong = async (question, userId) => {
    try {
      const response = await userAssignmentStatus({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        question_id: question.question_id,
        user_id: userId,
        answer_status: 'wrong'
      });

      if (response.success) {
        setSubmissions(prevSubmissions =>
          prevSubmissions.map(submission => {
            if (submission.user_id === userId) {
              return {
                ...submission,
                questions: submission.questions.map(q =>
                  q.question_id === question.question_id
                    ? { ...q, answer_status: 'wrong' }
                    : q
                )
              };
            }
            return submission;
          })
        );
        toast.success(response.message || 'Answer marked as wrong');
      } else {
        toast.error(response.message || 'Failed to update answer status');
      }
    } catch (error) {
      console.error('Error updating answer status:', error);
      toast.error('An error occurred while updating the answer status');
    }
  };

  // Handle marking answer as right
  const handleMarkRight = async (question, userId) => {
    try {
      const response = await userAssignmentStatus({
        class_id: decodedClassroomId,
        assignment_id: decodedAssignmentId,
        question_id: question.question_id,
        user_id: userId,
        answer_status: 'right'
      });

      if (response.success) {
        setSubmissions(prevSubmissions =>
          prevSubmissions.map(submission => {
            if (submission.user_id === userId) {
              return {
                ...submission,
                questions: submission.questions.map(q =>
                  q.question_id === question.question_id
                    ? { ...q, answer_status: 'right' }
                    : q
                )
              };
            }
            return submission;
          })
        );
        toast.success(response.message || 'Answer marked as right');
      } else {
        toast.error(response.message || 'Failed to update answer status');
      }
    } catch (error) {
      console.error('Error updating answer status:', error);
      toast.error('An error occurred while updating the answer status');
    }
  };

  // Render media based on type
  const renderMedia = (url, type, altText = 'Media') => {
    if (!url) return null;

    if (type === 'image') {
      return <img src={url} alt={altText} className="w-full mb-3 rounded" style={{ maxWidth: '100%', maxHeight: '300px' }} />;
    } else if (type === 'audio') {
      return (
        <div className="mb-3">
          <audio controls className="w-full" style={{ minHeight: '50px' }}>
            <source src={url} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }
    return null;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Search section */}
      <div className="flex mb-3">
        <div className="w-full md:w-1/2 mb-2">
          <input
            type="text"
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Search User"
            style={{ width: '300px' }}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>

      {/* table */}
      <div className="flex mb-3">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            {loading ? (
              <div className="flex justify-center my-5">
                <Loader />
              </div>
            ) : submissions.length === 0 ? (
              <div className="flex justify-center my-5">
                <Nodata message="No submissions found" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse mb-0">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '80px' }}>Sl No</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>User Name</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Email</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Total Questions</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Attended</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500' }}>Remaining</th>
                      <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission, index) => {
                      const isExpanded = expandedRows[submission.user_id];
                      return (
                        <Fragment key={submission.user_id || index}>
                          <tr className="border-bottom">
                            <td className="py-3 align-middle">{(page - 1) * limit + index + 1}</td>
                            <td className="py-3 align-middle">{submission.user_name || 'N/A'}</td>
                            <td className="py-3 align-middle">{submission.email || 'N/A'}</td>
                            <td className="py-3 align-middle">
                              <span className="bg-primary text-white px-2 py-1 rounded text-sm font-medium">{submission.total_questions || 0}</span>
                            </td>
                            <td className="py-3 align-middle">
                              <span className="bg-green-500 text-white px-2 py-1 rounded text-sm font-medium">{submission.attended_questions || 0}</span>
                            </td>
                            <td className="py-3 align-middle">
                              <span className="bg-yellow-500 text-gray-900 px-2 py-1 rounded text-sm font-medium">{submission.remaining_questions || 0}</span>
                            </td>
                            <td className="py-3 align-middle">
                              <button
                                className="border border-gray-300 text-primary hover:bg-primary/10 font-medium py-1 px-2 rounded-lg text-sm"
                                style={{ width: '36px', height: '36px' }}
                                title={!permissions.classroom_assignment_response_view_analytics ? "You don't have permission to view response details" : (isExpanded ? 'Hide details' : 'View details')}
                                onClick={() => toggleRowExpand(submission.user_id)}
                                disabled={!permissions.classroom_assignment_response_view_analytics}
                              >
                                <Icon
                                  icon={isExpanded ? "fluent:chevron-up-24-regular" : "fluent:chevron-down-24-regular"}
                                  width="16"
                                  height="16"
                                />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && permissions.classroom_assignment_response_view_analytics && (
                            <tr>
                              <td colSpan={7} className="p-0">
                                <div className="border-t border-gray-200 bg-gray-50">
                                  <div className="p-3 border-b border-gray-200">
                                    <h5 className="mb-0 font-light">Questions & Answers</h5>
                                  </div>
                                  <div className="p-4">
                                    {submission.questions && submission.questions.length > 0 ? (
                                      submission.questions.map((question, qIndex) => (
                                        <div key={qIndex} className="bg-white rounded-lg shadow-sm border border-gray-100 mb-4">
                                          <div className="p-4">
                                            <div className="flex justify-between items-center mb-3">
                                              <h6 className="text-gray-500 mb-0">
                                                <span className="font-bold">Question {qIndex + 1}</span>
                                              </h6>
                                              <div className="flex gap-2">
                                                <button
                                                  className={`text-sm font-medium py-1 px-3 rounded-lg ${
                                                    question.answer_status === 'wrong' 
                                                      ? 'bg-red-500 text-white' 
                                                      : 'border border-red-500 text-red-500 hover:bg-red-50'
                                                  }`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (question.answer_status !== 'wrong') {
                                                      handleMarkWrong(question, submission.user_id);
                                                    }
                                                  }}
                                                  disabled={question.answer_status === 'wrong'}
                                                >
                                                  Wrong
                                                </button>
                                                <button
                                                  className={`text-sm font-medium py-1 px-3 rounded-lg ${
                                                    question.answer_status === 'right' 
                                                      ? 'bg-green-500 text-white' 
                                                      : 'border border-green-500 text-green-500 hover:bg-green-50'
                                                  }`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (question.answer_status !== 'right') {
                                                      handleMarkRight(question, submission.user_id);
                                                    }
                                                  }}
                                                  disabled={question.answer_status === 'right'}
                                                >
                                                  Right
                                                </button>
                                              </div>
                                            </div>

                                            {/* Question Content */}
                                            <div className="mb-3 p-3 bg-gray-50 rounded">
                                              <h6 className="mb-2 font-light">Question:</h6>
                                              {question.question_text && (
                                                <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{question.question_text}</p>
                                              )}

                                              {/* Question Media */}
                                              {question.question_media_url && (
                                                <div className="mt-2">
                                                  <h6 className="mb-2 font-light">Question Media:</h6>
                                                  {renderMedia(question.question_media_url, question.question_type === 'image' ? 'image' : 'audio')}
                                                </div>
                                              )}
                                            </div>

                                            {/* User's Answer */}
                                            <div className="mb-3 p-3 bg-gray-50 rounded">
                                              <h6 className="mb-2">
                                                <span className="bg-primary text-white px-2 py-1 rounded text-sm font-medium mr-2">Trainee Answer</span>
                                              </h6>

                                              {/* Text Response */}
                                              {question.response_type === 'text_response' && question.answer_text && (
                                                <div className="p-2 bg-white rounded">
                                                  <p className="mb-0">{question.answer_text}</p>
                                                </div>
                                              )}

                                              {/* Image Response */}
                                              {question.response_type === 'image_response' && question.submission_media_url && (
                                                <div>
                                                  {renderMedia(question.submission_media_url, 'image', 'Trainee\'s image response')}
                                                </div>
                                              )}

                                              {/* Audio Response */}
                                              {question.response_type === 'audio_response' && question.submission_media_url && (
                                                <div>
                                                  <h6 className="mb-2">Audio Response:</h6>
                                                  {renderMedia(question.submission_media_url, 'audio')}
                                                </div>
                                              )}

                                              {/* No answer provided */}
                                              {!question.answer_text && !question.submission_media_url && (
                                                <div className="text-gray-500">No answer provided</div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <p className="text-gray-500">No question details available</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* record per page and pagination */}
      {!loading && submissions.length > 0 && (
        <div className="flex mt-4 pt-4 border-t border-gray-100">
          <div className="md:w-1/2 flex flex-row items-center">
            <select
              className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              style={{ width: 'auto' }}
              value={limit}
              onChange={handleLimitChange}
            >
              <option value={5}>5 records per page</option>
              <option value={10}>10 records per page</option>
              <option value={25}>25 records per page</option>
              <option value={50}>50 records per page</option>
            </select>
            <small className="text-gray-500 ml-2">
              Total {totalSubmissions} records
            </small>
          </div>
          <div className="md:w-1/2 flex justify-end items-center gap-3">
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
              style={{ width: '150px' }}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
              style={{ width: '150px' }}
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ClassroomAssignmentResponse
